import { languageNameFromCode } from './languageDisplay';

export interface LanguageLocale {
    code: string;
    [key: string]: any;
}

export interface LangOption {
    value: string;
    label: string;
}

const regionDisplay = new Intl.DisplayNames(['en'], { type: 'region' });
const scriptDisplay = new Intl.DisplayNames(['en'], { type: 'script' });

export function langPrefix(code: string): string {
    return code.includes('-') ? code.split('-')[0] : code;
}

const isTwoLetterCode = (prefix: string) => /^[a-z]{2}$/.test(prefix);

export function safeRegionName(regionCode: string): string {
    try { return regionDisplay.of(regionCode) || regionCode; } catch { return regionCode; }
}

function safeScriptName(scriptCode: string): string {
    try { return scriptDisplay.of(scriptCode) || scriptCode; } catch { return scriptCode; }
}

// BCP-47 variant subtags that Intl.DisplayNames can't resolve at all (there's
// no region/script code for them) — e.g. "WLS" in "en-GB-WLS" (Welsh variant).
const VARIANT_SUBTAG_OVERRIDES: Record<string, string> = {
    WLS: 'Welsh',
};

const isRegionSubtag = (part: string) => /^[A-Z]{2}$/.test(part);
const isScriptSubtag = (part: string) => /^[A-Z][a-z]{3}$/.test(part);

// Resolves a locale's accent suffix to a readable label, handling multi-part
// suffixes (script+region, variant) that Intl's region type can't parse alone.
function resolveAccentSuffix(suffix: string): string {
    const parts = suffix.split('-');
    if (parts.length === 1) {
        const part = parts[0];
        if (VARIANT_SUBTAG_OVERRIDES[part]) return VARIANT_SUBTAG_OVERRIDES[part];
        if (isScriptSubtag(part)) return safeScriptName(part);
        return safeRegionName(part);
    }

    const variantPart = parts.find(p => VARIANT_SUBTAG_OVERRIDES[p]);
    if (variantPart) return VARIANT_SUBTAG_OVERRIDES[variantPart];

    const scriptPart = parts.find(isScriptSubtag);
    const regionPart = parts.find(isRegionSubtag);
    const leftover = parts.filter(p => p !== scriptPart && p !== regionPart);

    if (scriptPart && regionPart) return `${safeScriptName(scriptPart)} (${safeRegionName(regionPart)})`;
    // Dialect suffixes (e.g. "CN-guangxi") don't map to a script/region on their own.
    if (regionPart && leftover.length > 0) return `${leftover.map(capitalize).join(' ')} (${safeRegionName(regionPart)})`;
    if (regionPart) return safeRegionName(regionPart);

    return parts.map(capitalize).join(' ');
}

function capitalize(part: string): string {
    return part.length ? part.charAt(0).toUpperCase() + part.slice(1) : part;
}

// Groups locales by resolved language name, merging ISO subtags that mean the
// same language (e.g. "ar"/"arb" both "Arabic") into one canonical entry.
export function buildLanguageGroups<T extends LanguageLocale>(
    languages: Record<string, T> | null | undefined
): Record<string, T[]> {
    if (!languages) return {};

    const rawGroups: Record<string, T[]> = {};
    Object.values(languages).forEach(lang => {
        const prefix = langPrefix(lang.code);
        if (!rawGroups[prefix]) rawGroups[prefix] = [];
        rawGroups[prefix].push(lang);
    });

    const byLabel: Record<string, { canonicalPrefix: string; langs: T[] }> = {};
    Object.keys(rawGroups).forEach(prefix => {
        const label = languageNameFromCode(prefix);
        const existing = byLabel[label];
        if (!existing) {
            byLabel[label] = { canonicalPrefix: prefix, langs: rawGroups[prefix] };
        } else {
            // Prefer the ISO 639-1 two-letter code as canonical (e.g. "ar" over "arb").
            const candidateIsTwoLetter = isTwoLetterCode(prefix);
            const existingIsTwoLetter = isTwoLetterCode(existing.canonicalPrefix);
            let canonicalPrefix = existing.canonicalPrefix;
            if (candidateIsTwoLetter && !existingIsTwoLetter) canonicalPrefix = prefix;
            else if (candidateIsTwoLetter === existingIsTwoLetter && prefix.length < existing.canonicalPrefix.length) canonicalPrefix = prefix;
            byLabel[label] = { canonicalPrefix, langs: [...existing.langs, ...rawGroups[prefix]] };
        }
    });

    const groups: Record<string, T[]> = {};
    Object.values(byLabel).forEach(({ canonicalPrefix, langs }) => {
        groups[canonicalPrefix] = langs;
    });
    return groups;
}

/** One row per merged language group, sorted alphabetically by label. */
export function buildLanguageOptions(langGroups: Record<string, LanguageLocale[]>): LangOption[] {
    return Object.keys(langGroups)
        .map(prefix => ({ value: prefix, label: languageNameFromCode(prefix) }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Accent (specific locale) options for one language group — only meaningful
 * when the group has more than one locale; returns [] otherwise (caller
 * should hide the accent picker in that case).
 */
export function buildAccentOptions(
    langGroups: Record<string, LanguageLocale[]>,
    prefix: string | null | undefined
): LangOption[] {
    if (!prefix || !langGroups[prefix]) return [];
    const entries = langGroups[prefix];
    if (entries.length <= 1) return [];

    return entries
        .map(lang => {
            const suffix = lang.code.split('-').slice(1).join('-');
            const name = suffix ? resolveAccentSuffix(suffix) : 'Default';
            return { value: lang.code, label: name };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
}

// Maps a locale code to its canonical langGroups key (e.g. "arb" -> "ar"), since merged groups can differ from langPrefix(code).
export function resolveGroupPrefix(
    langGroups: Record<string, LanguageLocale[]>,
    code: string | null | undefined
): string {
    if (!code) return '';
    const rawPrefix = langPrefix(code);
    if (langGroups[rawPrefix]) return rawPrefix;

    const match = Object.entries(langGroups).find(([, entries]) =>
        entries.some(lang => lang.code === code || langPrefix(lang.code) === rawPrefix)
    );
    return match ? match[0] : rawPrefix;
}
